import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ChartBar, CalendarCheck, Hourglass, Stethoscope, CheckCircle, XCircle, Users, CurrencyCircleDollar, CaretDown, WarningCircle, Prohibit } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import AddWalkinDialog from "@/components/AddWalkinDialog";
import BrandedPageLoader from "@/components/BrandedPageLoader";
import { CardSkeleton } from "@/components/LoadingSkeleton";

const FILTERS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "all", label: "All" },
];

function dateBounds(filter) {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const d = now.getUTCDate();
  if (filter === "today") {
    const s = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    return { start_date: s, end_date: s };
  }
  if (filter === "week") {
    const monday = new Date(Date.UTC(y, m, d));
    const dow = (monday.getUTCDay() + 6) % 7;
    monday.setUTCDate(monday.getUTCDate() - dow);
    return { start_date: `${monday.getUTCFullYear()}-${String(monday.getUTCMonth() + 1).padStart(2, "0")}-${String(monday.getUTCDate()).padStart(2, "0")}` };
  }
  if (filter === "month") {
    return { start_date: `${y}-${String(m + 1).padStart(2, "0")}-01` };
  }
  return {};
}

export default function DoctorDashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [walkinOpen, setWalkinOpen] = useState(false);
  const [filter, setFilter] = useState("today");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const loadingRef = useRef(false);

  const load = useCallback(async (f) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const profRes = await api.get("/doctors/me");
      setProfile(profRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        navigate("/doctor-pending", { replace: true });
        loadingRef.current = false;
        return;
      }
    }
    const params = dateBounds(f);
    const [allRes] = await Promise.allSettled([
      api.get("/appointments/all", { params }),
    ]);
    if (allRes.status === "fulfilled") {
      const items = allRes.value.data.appointments || [];
      items.sort((a, b) => new Date(b.created_at || b.queue_date) - new Date(a.created_at || a.queue_date));
      setAppointments(items);
    }
    setLoading(false);
    loadingRef.current = false;
  }, [navigate]);

  useEffect(() => { load(filter); }, [filter, load]);

  useEffect(() => {
    if (!profile?.subscription) return;
    const sub = profile.subscription;
    if (sub.subscription_status === "expired" || sub.subscription_status === "suspended") {
      setShowExpiryDialog(true);
    }
  }, [profile]);

  const summary = useMemo(() => {
    const s = { waiting: 0, in_progress: 0, completed: 0, cancelled: 0, total: appointments.length };
    appointments.forEach((a) => {
      if (s[a.status] !== undefined) s[a.status]++;
    });
    return s;
  }, [appointments]);

  const fee = profile?.consultation_fee || 0;
  const revenue = summary.completed * fee;

  const filterObj = FILTERS.find((f) => f.value === filter);
  const filterLabel = filterObj?.label || "Today";
  const titleLabel = filter === "all" ? "All Bookings" : `${filterLabel}'s`;

  const cards = [
    { label: "Total", value: summary.total, icon: CalendarCheck, color: "#224032", bg: "#E1ECE5" },
    { label: "Waiting", value: summary.waiting, icon: Hourglass, color: "#6B7F74", bg: "#F2EFE9" },
    { label: "In progress", value: summary.in_progress, icon: Stethoscope, color: "#C57053", bg: "#FBE8DF" },
    { label: "Completed", value: summary.completed, icon: CheckCircle, color: "#224032", bg: "#E1ECE5" },
    { label: "Cancelled", value: summary.cancelled, icon: XCircle, color: "#8A2A2A", bg: "#F5E1E1" },
  ];

  const maxVal = Math.max(summary.total, 1);

  const sub = profile?.subscription || {};
  const expiryDialogStatus = sub.subscription_status;
  const isTrialExpired = expiryDialogStatus === "expired" && (sub.subscription_plan === "trial" || !sub.subscription_plan);
  const canRequestExtension = isTrialExpired
    && !sub.trial_extension_used
    && sub.trial_extension_request_status !== "pending"
    && sub.trial_extension_request_status !== "rejected";

  if (loading) {
    return (
      <BrandedPageLoader
        message="Loading dashboard"
        subtext="Preparing your appointments, queue, and revenue"
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="workspace-page-flush py-3 sm:py-4">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8 sm:mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Dashboard</div>
            <h1 className="font-display font-bold text-foreground text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {profile?.name ? `Welcome, ${profile.name}` : "Welcome"}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {profile?.clinic_name && <>{profile.clinic_name} · </>}
              {summary.total} appointment{summary.total !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {profile?.tv_display_enabled && (
              <button
                type="button"
                onClick={() => window.open("/doctor/queue-display", "_blank")}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full border border-border text-foreground hover:bg-card transition-colors"
              >
                TV Display
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate("/doctor/queue")}
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-primary text-primary-foreground hover:bg-[#1a3025] transition-colors"
            >
              <Users size={16} weight="duotone" /> Open queue
            </button>
          </div>
        </div>

        {/* Subscription alert — banner for non-blocked warnings */}
        {profile?.subscription?.days_remaining != null && profile?.subscription?.days_remaining <= 7 && profile?.subscription?.days_remaining > 0 && (
          <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 mb-6 flex items-start gap-3">
            <WarningCircle size={20} className="text-yellow-600 shrink-0 mt-0.5" weight="duotone" />
            <div className="flex-1">
              <p className="font-semibold text-sm text-yellow-800">Your plan expires in {profile.subscription.days_remaining} day{profile.subscription.days_remaining !== 1 ? "s" : ""}. <button onClick={() => navigate("/plan-details")} className="underline font-bold">Renew now</button> to avoid interruption.</p>
            </div>
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {cards.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.label} className="rounded-2xl border border-border bg-card p-5 transition-shadow hover:shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: c.bg, color: c.color }}>
                    <Icon size={20} weight="duotone" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{c.label}</div>
                    <div className="font-display font-bold text-2xl text-foreground">{c.value}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Overview with filter + Revenue */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar chart: status breakdown */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <ChartBar size={18} weight="duotone" className="text-primary" />
                <h2 className="font-display font-bold text-lg text-foreground">{titleLabel} overview</h2>
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full border border-border bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  {filterLabel}
                  <CaretDown size={12} weight="bold" />
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-40 rounded-xl border border-border bg-card shadow-lg z-10 overflow-hidden">
                    {FILTERS.map((f) => (
                      <button
                        key={f.value}
                        type="button"
                        onClick={() => { setFilter(f.value); setDropdownOpen(false); }}
                        className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${filter === f.value ? "bg-primary/10 text-foreground font-semibold" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {loading ? (
              <CardSkeleton />
            ) : (
              <div className="space-y-4">
                {[
                  { label: "Waiting", value: summary.waiting, color: "#6B7F74", pct: (summary.waiting / maxVal) * 100 },
                  { label: "In progress", value: summary.in_progress, color: "#C57053", pct: (summary.in_progress / maxVal) * 100 },
                  { label: "Completed", value: summary.completed, color: "#224032", pct: (summary.completed / maxVal) * 100 },
                  { label: "Cancelled", value: summary.cancelled, color: "#8A2A2A", pct: (summary.cancelled / maxVal) * 100 },
                ].map((row) => (
                  <div key={row.label}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="font-semibold text-foreground">{row.label}</span>
                      <span className="text-muted-foreground">{row.value}</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.max(row.pct, row.value > 0 ? 2 : 0)}%`, backgroundColor: row.color }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Revenue */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-center gap-2 mb-5">
              <CurrencyCircleDollar size={18} weight="duotone" className="text-primary" />
              <h2 className="font-display font-bold text-lg text-foreground">Revenue</h2>
            </div>
            {loading ? (
              <CardSkeleton message="Loading revenue" />
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-muted p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Total revenue</div>
                  <div className="font-token text-2xl text-foreground mt-1">₹{revenue.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">₹{fee} per consult</div>
                </div>
                <div className="rounded-xl bg-muted p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Patients</div>
                  <div className="font-token text-2xl text-foreground mt-1">{appointments.length - summary.cancelled}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">served</div>
                </div>
                <div className="rounded-xl bg-muted p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Cancelled</div>
                  <div className="font-token text-2xl text-foreground mt-1">{summary.cancelled}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{summary.total ? Math.round((summary.cancelled / summary.total) * 100) : 0}% rate</div>
                </div>
                <div className="rounded-xl bg-muted p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Completed</div>
                  <div className="font-token text-2xl text-foreground mt-1">{summary.completed}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{summary.total ? Math.round((summary.completed / summary.total) * 100) : 0}% rate</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* FAB */}
      <button
        type="button"
        onClick={() => setWalkinOpen(true)}
        className="fixed bottom-6 left-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-[#1a3025] transition-colors flex items-center justify-center"
        title="New appointment"
      >
        <Users size={24} weight="duotone" />
      </button>

      <AddWalkinDialog open={walkinOpen} onClose={() => { setWalkinOpen(false); load(filter); }} />

      {/* Expiry / Suspended Dialog */}
      {showExpiryDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
          <div className="bg-card rounded-2xl border border-border w-full max-w-md shadow-2xl">
            <div className="p-6 text-center">
              <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${expiryDialogStatus === "suspended" ? "bg-amber-100" : "bg-red-100"}`}>
                {expiryDialogStatus === "suspended" ? (
                  <WarningCircle size={32} className="text-amber-600" weight="duotone" />
                ) : (
                  <Prohibit size={32} className="text-red-600" weight="duotone" />
                )}
              </div>
              <h2 className="font-display font-bold text-xl text-foreground mb-2">
                {expiryDialogStatus === "suspended" ? "Subscription Suspended" : "Plan Expired"}
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                {expiryDialogStatus === "suspended"
                  ? "Your subscription is suspended. Please contact admin."
                  : isTrialExpired
                    ? "Your trial has expired. Your profile is hidden from patients and new bookings are disabled."
                    : "Your plan has expired. Your profile is hidden from patients and new bookings are disabled."}
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => { setShowExpiryDialog(false); navigate("/plan-details"); }}
                  className="w-full py-3 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-[#1a3025] transition-colors"
                >
                  Upgrade Plan
                </button>
                {canRequestExtension && (
                  <button
                    onClick={async () => {
                      try {
                        await api.post("/doctors/me/trial-extension-request", { message: "" });
                        setShowExpiryDialog(false);
                      } catch (err) {
                        // dialog stays open on error
                      }
                    }}
                    className="w-full py-3 rounded-full border border-border bg-muted text-foreground font-semibold text-sm hover:bg-muted/80 transition-colors"
                  >
                    Request Trial Extension
                  </button>
                )}
                <button
                  onClick={() => { setShowExpiryDialog(false); }}
                  className="w-full py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  View Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
